import initRuff, { PositionEncoding, Workspace } from '@astral-sh/ruff-wasm-web';
import type * as monaco from 'monaco-editor';
import { findMatchingDelimiterIndex, findTopLevelKeywordAssignmentIndex, splitTopLevelByComma } from './pythonSourceScanUtils';

/** Ruff default `line-length`; drives when `formatGeneratedPythonCall` expands multiline calls. */
export const RUFF_DEFAULT_LINE_LENGTH = 88;

let ruffWorkspacePromise: Promise<Workspace> | null = null;

async function getRuffWorkspace(): Promise<Workspace> {
  ruffWorkspacePromise ??= initRuff().then(() => new Workspace(Workspace.defaultSettings(), PositionEncoding.Utf16));
  return ruffWorkspacePromise;
}

export async function formatPythonProgramWithRuff(code: string): Promise<string> {
  try {
    const workspace = await getRuffWorkspace();
    const formatted = workspace.format(code);
    return formatted ? restorePythonRegionMarkers(formatted) : code;
  } catch {
    return formatGeneratedPythonCallsInProgram(code);
  }
}

type PythonFormattingModel = Pick<monaco.editor.ITextModel, 'getFullModelRange' | 'getValue'>;
type PythonFormattingEditor = Pick<monaco.editor.IStandaloneCodeEditor, 'executeEdits' | 'pushUndoStop'> & {
  getModel: () => PythonFormattingModel | null;
};

export async function formatActivePythonProgram(
  editor: PythonFormattingEditor,
  formatter: (code: string) => Promise<string> = formatPythonProgramWithRuff
): Promise<string | null> {
  const model = editor.getModel();
  if (!model) return null;

  const currentCode = model.getValue();
  const formattedCode = await formatter(currentCode);
  if (editor.getModel() !== model || formattedCode === currentCode) return null;

  return applyFormattedPythonProgram(editor, model, formattedCode);
}

export function applyFormattedPythonProgram(
  editor: Pick<monaco.editor.IStandaloneCodeEditor, 'executeEdits' | 'pushUndoStop'>,
  model: Pick<monaco.editor.ITextModel, 'getFullModelRange' | 'getValue'>,
  formattedCode: string
): string {
  editor.pushUndoStop();
  editor.executeEdits('python.formatProgram', [{ range: model.getFullModelRange(), text: formattedCode, forceMoveMarkers: true }]);
  editor.pushUndoStop();
  return model.getValue();
}

export function restorePythonRegionMarkers(code: string): string {
  return code.replace(/^(\s*)#\s+(end)?region\b/gim, '$1#$2region');
}

function findLastCompleteTopLevelCallSpan(code: string): { open: number; close: number } | null {
  let candidate: { open: number; close: number } | null = null;

  for (let index = 0; index < code.length; index += 1) {
    const ch = code[index] ?? '';
    if (ch !== '(') continue;

    const close = findMatchingDelimiterIndex(code, index, '(', ')', { ignoreLineComments: true });
    if (close < 0) return null;
    if (code.slice(close + 1).trim().length === 0) {
      candidate = { open: index, close };
    }
    index = close;
  }

  return candidate;
}

const trimSingleTrailingComma = (value: string): { core: string; trailingComma: string } => {
  const trimmedEnd = value.trimEnd();
  if (!trimmedEnd.endsWith(',')) {
    return { core: value, trailingComma: '' };
  }

  return {
    core: `${trimmedEnd.slice(0, -1)}${value.slice(trimmedEnd.length)}`,
    trailingComma: ',',
  };
};

function parseListLiteralArg(arg: string): { trimmed: string; items: string[] } | null {
  const trimmed = arg.trim();
  if (!trimmed.startsWith('[')) return null;

  const close = findMatchingDelimiterIndex(trimmed, 0, '[', ']', { ignoreLineComments: true });
  if (close < 0 || trimmed.slice(close + 1).trim().length > 0) return null;

  const items = splitTopLevelByComma(trimmed.slice(1, close)).filter((item) => item.length > 0);
  return { trimmed, items };
}

function shouldExpandCall(original: string, args: string[]): boolean {
  if (args.length > 1 && (original.length > RUFF_DEFAULT_LINE_LENGTH || args.length >= 4 || original.includes('\n'))) {
    return true;
  }

  return (
    args.length === 1 &&
    original.length > RUFF_DEFAULT_LINE_LENGTH &&
    (parseListLiteralArg(args[0]) != null || formatKeywordListArg(args[0]) != null || formatSingleNestedCallArg(args[0]) != null)
  );
}

function formatListLiteralLines(value: string, indent: string): string[] | null {
  const parsed = parseListLiteralArg(value);
  if (!parsed) return null;

  const { items } = parsed;
  if (items.length < 2) return null;

  return [`${indent}[`, ...items.map((item) => `${indent}    ${item},`), `${indent}],`];
}

function formatSingleListArg(arg: string, indent = '    '): string[] | null {
  const parsed = parseListLiteralArg(arg);
  if (!parsed) return null;

  const { trimmed, items } = parsed;
  if (items.length < 2) return null;

  const singleLineArg = `${indent}${trimmed}`;
  if (singleLineArg.length <= RUFF_DEFAULT_LINE_LENGTH) {
    return null;
  }

  return formatListLiteralLines(arg, indent);
}

function formatKeywordListArg(arg: string, indent = '    '): string[] | null {
  const assignmentIndex = findTopLevelKeywordAssignmentIndex(arg);
  if (assignmentIndex <= 0) return null;

  const name = arg.slice(0, assignmentIndex).trim();
  const value = arg.slice(assignmentIndex + 1).trim();
  const formattedValue = formatListLiteralLines(value, indent);
  if (!formattedValue) return null;

  const [openLine, ...rest] = formattedValue;
  return [`${indent}${name}=${openLine.trimStart()}`, ...rest];
}

function formatSingleNestedCallArg(arg: string, indent = '    '): string[] | null {
  const formatted = formatGeneratedPythonCall(arg.trim());
  if (formatted === arg.trim() || !formatted.includes('\n')) return null;

  const lines = formatted.split('\n').map((line) => `${indent}${line}`);
  lines[lines.length - 1] = `${lines[lines.length - 1]},`;
  return lines;
}

function formatCallArgs(args: string[]): string[] {
  if (args.length === 1) {
    const listArg = formatSingleListArg(args[0]);
    if (listArg) {
      return listArg;
    }

    const keywordListArg = formatKeywordListArg(args[0]);
    if (keywordListArg) {
      return keywordListArg;
    }

    const nestedCallArg = formatSingleNestedCallArg(args[0]);
    if (nestedCallArg) {
      return nestedCallArg;
    }

    return [`    ${args[0].trim()}`];
  }

  return args.flatMap((arg) => formatKeywordListArg(arg) ?? formatSingleNestedCallArg(arg) ?? [`    ${arg},`]);
}

export function formatGeneratedPythonCall(code: string): string {
  const leading = code.match(/^\s*/)?.[0] ?? '';
  const trailing = code.match(/\s*$/)?.[0] ?? '';
  const body = code.slice(leading.length, code.length - trailing.length);
  const { core, trailingComma } = trimSingleTrailingComma(body);
  const span = findLastCompleteTopLevelCallSpan(core);
  if (!span) return code;

  const argsText = core.slice(span.open + 1, span.close);
  const args = splitTopLevelByComma(argsText).filter((arg) => arg.length > 0);
  if (!shouldExpandCall(core, args)) return code;

  const callee = core.slice(0, span.open).trimEnd();
  const formatted = [callee + '(', ...formatCallArgs(args), ')'].join('\n');
  return `${leading}${formatted}${trailingComma}${trailing}`;
}

export function formatGeneratedPythonCallsInProgram(code: string): string {
  const lineEnding = code.includes('\r\n') ? '\r\n' : '\n';
  return code
    .split(/\r?\n/)
    .map((line) => formatGeneratedPythonCall(line))
    .join(lineEnding);
}
